import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reset-password landing page. Arrives via `?token=…` from the email
 * link. Prompts for a new password twice; on success, deep-links the
 * user back to /login with a toast that they can now sign in.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (pwd !== pwd2) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: pwd,
      });
      setDone(true);
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="mobile-shell px-5 pt-10 min-h-screen" data-testid="reset-password-no-token">
        <h1 className="font-serif-display text-xl">Invalid reset link</h1>
        <p className="text-sm text-stone-500 mt-2">
          This link is missing a token. Request a fresh reset from the login page.
        </p>
        <Link
          to="/forgot-password"
          data-testid="reset-password-request-new"
          className="mt-6 inline-flex items-center gap-1 text-sm tenant-text font-semibold"
        >
          Request new link
        </Link>
      </div>
    );
  }

  if (done) {
    return (
      <div className="mobile-shell px-5 pt-10 min-h-screen text-center" data-testid="reset-password-done">
        <div className="w-14 h-14 rounded-full grid place-items-center mx-auto mb-4 bg-emerald-100">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" strokeWidth={1.8} />
        </div>
        <h1 className="font-serif-display text-2xl">Password updated</h1>
        <p className="text-sm text-stone-500 mt-2">Redirecting you to sign in…</p>
      </div>
    );
  }

  return (
    <div className="mobile-shell px-5 pt-6 min-h-screen" data-testid="reset-password">
      <Link
        to="/login"
        data-testid="reset-password-back"
        className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-700"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Sign in
      </Link>

      <div className="mt-6 flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl grid place-items-center"
             style={{ backgroundColor: "color-mix(in srgb, var(--tenant-primary) 12%, transparent)" }}>
          <KeyRound className="w-5 h-5 tenant-text" strokeWidth={1.8} />
        </div>
        <div>
          <h1 className="font-serif-display text-2xl leading-none">Set new password</h1>
          <p className="text-xs text-stone-500 mt-1">Choose something you&apos;ll remember.</p>
        </div>
      </div>

      <form onSubmit={submit} className="mt-6 space-y-4">
        <div>
          <Label className="text-xs">New password</Label>
          <PasswordInput
            data-testid="reset-password-pwd"
            required
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            minLength={6}
            maxLength={128}
            autoComplete="new-password"
            className="bg-white"
          />
          <div className="text-[10px] text-stone-400 mt-1">Min 6 characters.</div>
        </div>
        <div>
          <Label className="text-xs">Confirm new password</Label>
          <PasswordInput
            data-testid="reset-password-pwd2"
            required
            value={pwd2}
            onChange={(e) => setPwd2(e.target.value)}
            minLength={6}
            maxLength={128}
            autoComplete="new-password"
            className="bg-white"
          />
        </div>
        <Button
          type="submit"
          disabled={loading || pwd.length < 6 || pwd !== pwd2}
          data-testid="reset-password-submit"
          className="w-full rounded-full text-white h-11"
          style={{ backgroundColor: "var(--tenant-primary)" }}
        >
          {loading ? "Saving…" : "Update password"}
        </Button>
      </form>
    </div>
  );
}
